import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Users, UserPlus, Trash2, Shield } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export interface UserRole {
  user: string;
  role: string;
}

export interface RoleManagerProps {
  assignments: UserRole[];
  availableRoles?: string[];
  onAssign: (user: string, role: string) => void;
  onUnassign: (user: string, role: string) => void;
}

export function RoleManager({ 
  assignments, 
  onAssign, 
  onUnassign,
  availableRoles = ['admin', 'developer', 'viewer', 'member', 'billing']
}: RoleManagerProps) {
  const [newUser, setNewUser] = useState('');
  const [newRole, setNewRole] = useState('member');

  const handleAssign = () => {
    if (newUser && newRole) {
      onAssign(newUser, newRole);
      setNewUser('');
    }
  };

  return (
    <Card className="w-full border-slate-200 shadow-sm">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5 text-blue-600" />
          Role Assignments
        </CardTitle>
        <CardDescription>
          Manage who has what role (RBAC). These roles are used in your policies.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Add New Assignment */}
        <div className="flex gap-3 p-4 bg-slate-50 rounded-lg border border-slate-100">
          <div className="flex-1">
            <Input 
              placeholder="User Email or ID (e.g. alice@example.com)" 
              value={newUser}
              onChange={(e) => setNewUser(e.target.value)}
              className="bg-white"
            />
          </div>
          <Select value={newRole} onValueChange={setNewRole}>
            <SelectTrigger className="w-[180px] bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {availableRoles.map(role => (
                <SelectItem key={role} value={role}>
                  <div className="flex items-center gap-2">
                    <Shield className="h-3 w-3 text-slate-400" />
                    {role}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={handleAssign} disabled={!newUser}>
            <UserPlus className="h-4 w-4 mr-2" /> Assign Role
          </Button>
        </div>

        {/* Assignments Table */}
        <div className="border rounded-md">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead>User / Principal</TableHead>
                <TableHead>Assigned Role</TableHead>
                <TableHead className="w-[100px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {assignments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} className="text-center py-8 text-slate-500 italic">
                    No roles assigned yet.
                  </TableCell>
                </TableRow>
              ) : (
                assignments.map((a, idx) => (
                  <TableRow key={`${a.user}-${a.role}-${idx}`}>
                    <TableCell className="font-medium">{a.user}</TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="bg-blue-50 text-blue-700 hover:bg-blue-100 border-blue-200">
                        {a.role}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-slate-400 hover:text-red-600 hover:bg-red-50"
                        onClick={() => onUnassign(a.user, a.role)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
